import {
    api,
    apiErrorMessage,
    ListChoiceFilter,
    useIsPlatformHost,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    CheckIcon,
    EditIcon,
    Field,
    FormSheet,
    Input,
    PageHeader,
    RetryIcon,
    RevokeIcon,
    RowActions,
    StatusBadge,
    Textarea,
    type ListColumnDef,
} from '@bemor/web-kit/ui';
import { useState } from 'react';

import type { Template } from '@/api/types';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';
import { useWrite } from '@/lib/queries';
import { ADMIN_STATUSES } from '@/lib/statuses';

type Draft = {
    email_subject: string;
    email_body: string;
    in_app_title: string;
    in_app_body: string;
};

/** Variables are {{name}}; the preview substitutes the catalogue's sample values. */
export function preview(
    text: string,
    variables: { name?: string; sample?: string }[],
): string {
    return text.replace(/\{\{\s*([a-z_.]+)\s*\}\}/gi, (_, name: string) => {
        const v = variables.find((x) => x.name === name);
        return v?.sample ?? `[${name}]`;
    });
}

const PATH = '/staff/notification-templates';
const PAGE_KEY = 'settings.templates';

/** The route's prefetch: the person's views, then the templates of the view (BM-46 §2.0 + §2.4). */
export const templatesPrefetch = adminListPrefetch(PAGE_KEY, PATH);

const templateFilters = filterDefs(PATH, {
    q: 'Search',
    category: 'Category',
    tenant_override: 'Copy',
    active: 'Active',
});

export function TemplatesPage() {
    // BM-105: the platform host edits the defaults every tenant inherits and marks which a tenant may override;
    // a tenant board overrides the copy of the templates opened to it
    const platformHost = useIsPlatformHost();
    const [editing, setEditing] = useState<Template | null>(null);
    const [draft, setDraft] = useState<Draft>({
        email_subject: '',
        email_body: '',
        in_app_title: '',
        in_app_body: '',
    });
    const [error, setError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<unknown>(null);
    const save = useWrite(
        async (body: { key: string; draft: Draft }) =>
            (
                await api().put<Template>(
                    `/staff/notification-templates/${body.key}`,
                    { ...body.draft, active: true },
                )
            ).data,
        ['/staff/notification-templates'],
    );
    const reset = useWrite(
        async (key: string) =>
            api().delete(`/staff/notification-templates/${key}`),
        ['/staff/notification-templates'],
    );
    // BM-103: Nexroll's activate / deactivate (an override with `active` set)
    const setActive = useWrite(
        async (body: { key: string; active: boolean }) =>
            (
                await api().post<Template>(
                    `/staff/notification-templates/${body.key}/${body.active ? 'activate' : 'deactivate'}`,
                )
            ).data,
        ['/staff/notification-templates'],
    );
    // BM-105: the platform opens or locks a default for tenant overrides
    const setOverridable = useWrite(
        async (body: { key: string; tenant_overridable: boolean }) =>
            (
                await api().put<Template>(
                    `/staff/notification-templates/${body.key}`,
                    { tenant_overridable: body.tenant_overridable },
                )
            ).data,
        ['/staff/notification-templates'],
    );
    const onError = (e: unknown) => setError(apiErrorMessage(e));
    const open = (t: Template) => {
        setEditing(t);
        setDraft({
            email_subject: t.email_subject ?? '',
            email_body: t.email_body ?? '',
            in_app_title: t.in_app_title ?? '',
            in_app_body: t.in_app_body ?? '',
        });
    };
    const columns: ListColumnDef<Template>[] = [
        {
            key: 'name',
            label: 'Template',
            sortKey: 'name',
            cell: (t) => (
                <span>
                    <span className="font-medium">{t.name}</span>{' '}
                    <span className="text-muted-foreground ml-1 font-mono text-xs">
                        {t.key}
                    </span>
                </span>
            ),
            locked: 'start',
        },
        {
            key: 'category',
            label: 'Category',
            sortKey: 'category',
            cell: (t) => t.category,
            groupable: { key: 'category', value: (t) => t.category },
        },
        {
            key: 'channels',
            label: 'Channels',
            cell: (t) => t.channels.join(', '),
        },
        {
            key: 'receivers',
            label: 'To',
            cell: (t) => t.receiver_types.join(', '),
        },
        {
            key: 'active',
            label: 'Status',
            sortKey: 'active',
            cell: (t) => (
                <StatusBadge
                    domain={ADMIN_STATUSES.templateActive}
                    status={t.active ? 'active' : 'inactive'}
                />
            ),
        },
        {
            key: 'override',
            label: platformHost ? 'Tenants' : 'Copy',
            // BM-105: the platform host shows whether tenants may override the default; a board shows its own copy state
            cell: (t) =>
                platformHost ? (
                    <StatusBadge
                        domain={ADMIN_STATUSES.templateOverridable}
                        status={t.tenant_overridable ? 'open' : 'locked'}
                    />
                ) : (
                    <StatusBadge
                        domain={ADMIN_STATUSES.template}
                        status={
                            t.tenant_override
                                ? 'overridden'
                                : t.tenant_overridable === false
                                  ? 'locked'
                                  : 'default'
                        }
                    />
                ),
        },
        {
            key: 'actions',
            label: 'Actions',
            locked: 'end',
            actions: true,
            // BM-84 (rule G.6, design 0.7.0 settings-templates): Edit copy, and Reset to default when overridden.
            // BM-105: a board edits only what the platform opened; the platform host opens or locks a default.
            cell: (t) => (
                <RowActions
                    name={t.name}
                    items={[
                        {
                            label: platformHost ? 'Edit default' : 'Edit copy',
                            icon: EditIcon,
                            hidden:
                                !platformHost && t.tenant_overridable === false,
                            onSelect: () => open(t),
                        },
                        {
                            label: t.active ? 'Deactivate' : 'Activate',
                            icon: t.active ? RevokeIcon : CheckIcon,
                            hidden:
                                !platformHost && t.tenant_overridable === false,
                            onSelect: () =>
                                setActive.mutate(
                                    { key: t.key, active: !t.active },
                                    { onError },
                                ),
                        },
                        {
                            label: t.tenant_overridable
                                ? 'Lock for tenants'
                                : 'Open to tenants',
                            icon: t.tenant_overridable ? RevokeIcon : CheckIcon,
                            hidden: !platformHost,
                            onSelect: () =>
                                setOverridable.mutate(
                                    {
                                        key: t.key,
                                        tenant_overridable:
                                            !t.tenant_overridable,
                                    },
                                    { onError },
                                ),
                        },
                        {
                            label: 'Reset to default',
                            icon: RetryIcon,
                            hidden: platformHost || !t.tenant_override,
                            destructive: true,
                            onSelect: () => reset.mutate(t.key, { onError }),
                        },
                    ]}
                />
            ),
        },
    ];
    const { state, query } = useAdminList<Template>(PAGE_KEY, PATH, {
        columns,
        filters: templateFilters,
    });
    // The categories on this page, and the chosen one (the contract lists no enum).
    const chosen = state.filters.category;
    const categories = [
        ...new Set([
            ...query.rows.map((t) => t.category),
            ...(chosen ? [chosen].flat() : []),
        ]),
    ].sort();
    const vars = (editing?.variables ?? []) as unknown as {
        name?: string;
        sample?: string;
    }[];
    const dirty =
        editing !== null &&
        (draft.email_subject !== (editing.email_subject ?? '') ||
            draft.email_body !== (editing.email_body ?? '') ||
            draft.in_app_title !== (editing.in_app_title ?? '') ||
            draft.in_app_body !== (editing.in_app_body ?? ''));
    const previewPane = editing ? (
        <div className="space-y-2 text-sm" data-testid="template-preview">
            <p className="text-muted-foreground text-xs">
                Preview with sample values
            </p>
            {editing.channels.includes('email') ? (
                <div className="rounded-md border p-3">
                    <p className="font-medium">
                        {preview(draft.email_subject, vars)}
                    </p>
                    <p className="mt-2 whitespace-pre-wrap">
                        {preview(draft.email_body, vars)}
                    </p>
                </div>
            ) : null}
            {editing.channels.includes('in_app') ? (
                <div className="rounded-md border p-3">
                    <p className="font-medium">
                        {preview(draft.in_app_title, vars)}
                    </p>
                    <p className="text-muted-foreground">
                        {preview(draft.in_app_body, vars)}
                    </p>
                </div>
            ) : null}
        </div>
    ) : null;
    return (
        <div className="space-y-4">
            <PageHeader
                title="Notification templates"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            {platformHost
                                ? 'The defaults every tenant inherits. Edit the copy, activate or deactivate, and open a template to tenant overrides; the variables and the channels stay.'
                                : 'The catalog Delivery sends from. Override the copy of the templates the platform opened; the variables and the channels stay.'}
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
            />
            {error ? (
                <p role="alert" className="text-destructive text-sm">
                    {error}
                </p>
            ) : null}
            <AdminListPage
                noun="notification templates"
                state={state}
                query={query}
                rowKey={(t) => t.key}
                exportable={false}
                controls={{
                    q: { kind: 'search' },
                    category: { options: categories },
                    tenant_override: (
                        <ListChoiceFilter
                            state={state}
                            name="tenant_override"
                            label="Copy"
                            options={[
                                { value: 'true', label: 'Overridden' },
                                { value: 'false', label: 'Default' },
                            ]}
                        />
                    ),
                    active: (
                        <ListChoiceFilter
                            state={state}
                            name="active"
                            label="Active"
                            options={[
                                { value: 'true', label: 'Active' },
                                { value: 'false', label: 'Inactive' },
                            ]}
                        />
                    ),
                }}
            />
            {/* BM-84 (rule E.9, design 0.7.0 settings-templates): the editor is a sheet with the live preview beside it */}
            <FormSheet
                open={editing !== null}
                onOpenChange={(o) => {
                    if (!o) {
                        setEditing(null);
                        setSaveError(null);
                    }
                }}
                size="lg"
                title={editing ? `Edit copy: ${editing.name}` : 'Edit copy'}
                description={
                    editing ? (
                        <span className="font-mono text-xs">{editing.key}</span>
                    ) : undefined
                }
                submitLabel={platformHost ? 'Save default' : 'Save override'}
                busy={save.isPending}
                dirty={dirty}
                error={saveError}
                aside={previewPane}
                data-testid="template-editor"
                onSubmit={(e) => {
                    e.preventDefault();
                    if (!editing) return;
                    save.mutate(
                        { key: editing.key, draft },
                        {
                            onSuccess: () => {
                                setEditing(null);
                                setSaveError(null);
                            },
                            onError: setSaveError,
                        },
                    );
                }}
            >
                {editing ? (
                    <>
                        <div className="space-y-3">
                            {editing.channels.includes('email') ? (
                                <>
                                    <Field
                                        id="email_subject"
                                        label="Email subject"
                                    >
                                        <Input
                                            id="email_subject"
                                            value={draft.email_subject}
                                            onChange={(e) =>
                                                setDraft({
                                                    ...draft,
                                                    email_subject:
                                                        e.target.value,
                                                })
                                            }
                                        />
                                    </Field>
                                    <Field id="email_body" label="Email body">
                                        <Textarea
                                            id="email_body"
                                            rows={8}
                                            value={draft.email_body}
                                            onChange={(e) =>
                                                setDraft({
                                                    ...draft,
                                                    email_body: e.target.value,
                                                })
                                            }
                                        />
                                    </Field>
                                </>
                            ) : null}
                            {editing.channels.includes('in_app') ? (
                                <>
                                    <Field
                                        id="in_app_title"
                                        label="In-app title"
                                    >
                                        <Input
                                            id="in_app_title"
                                            value={draft.in_app_title}
                                            onChange={(e) =>
                                                setDraft({
                                                    ...draft,
                                                    in_app_title:
                                                        e.target.value,
                                                })
                                            }
                                        />
                                    </Field>
                                    <Field id="in_app_body" label="In-app body">
                                        <Textarea
                                            id="in_app_body"
                                            rows={3}
                                            value={draft.in_app_body}
                                            onChange={(e) =>
                                                setDraft({
                                                    ...draft,
                                                    in_app_body: e.target.value,
                                                })
                                            }
                                        />
                                    </Field>
                                </>
                            ) : null}
                            <p className="text-muted-foreground text-xs">
                                Variables:{' '}
                                {vars.map((v) => `{{${v.name}}}`).join(' ') ||
                                    'none'}
                            </p>
                        </div>
                    </>
                ) : null}
            </FormSheet>
        </div>
    );
}
